import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Listing } from "@/types";
import { ConditionBadge } from "@/components/listings/ConditionBadge";
import {
  ListingFomoHighlight,
  listingComparisonInfo,
  isFreshListing,
} from "@/components/listings/ListingFomoBadges";
import { ListingCardBboxOverlay } from "@/components/listings/ListingCardBboxOverlay";
import { RatingBadge } from "@/components/ui/RatingBadge";
import { ListingPlaceholder } from "@/components/ui/ListingPlaceholder";
import { languageFlag } from "@/lib/languageFlags";

interface ListingCardProps {
  listing: Listing;
}

const STATUS_STYLES: Record<string, string> = {
  sold: "bg-red-600",
  reserved: "bg-amber-500",
  cancelled: "bg-gray-500",
};

export function ListingCard({ listing }: ListingCardProps) {
  const { t } = useTranslation();
  const imageUrl = listing.displayImageUrl || null;
  const hasBbox = imageUrl && listing.boundingBox && listing.siblingListings && listing.siblingListings.length > 0;

  const statusStyle = STATUS_STYLES[listing.status] ?? null;
  const statusLabel = statusStyle ? t(`listings.status_${listing.status}`) : null;

  const flag = languageFlag(listing.language);
  const languageTitle = listing.language
    ? t(`languages.${listing.language}`, listing.languageLabel ?? listing.language)
    : null;

  const comparisonInfo = listingComparisonInfo(listing.fomoBadges);
  const showFresh = isFreshListing(listing.fomoBadges);

  return (
    <Link
      to={`/listings/${listing.id}`}
      className="card group flex flex-col overflow-hidden transition-shadow hover:shadow-lg"
    >
      <div className="relative aspect-[3/2] w-full overflow-hidden bg-gray-100">
        {hasBbox && listing.boundingBox ? (
          <ListingCardBboxOverlay
            imageUrl={imageUrl}
            alt={listing.displayTitle}
            boundingBox={listing.boundingBox}
            siblings={listing.siblingListings}
            className="h-full w-full"
          />
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt={listing.displayTitle}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <ListingPlaceholder />
        )}
        {statusLabel && (
          <div className={`absolute right-2 top-2 rounded-full px-2 py-0.5 text-xs font-semibold text-white ${statusStyle}`}>
            {statusLabel}
          </div>
        )}
        {!statusLabel && <ListingFomoHighlight badges={listing.fomoBadges} />}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1.5 p-3">
        <h3 className="line-clamp-2 text-sm font-semibold text-gray-900 group-hover:text-primary-800">
          {listing.displayTitle}
        </h3>

        {listing.price != null && (
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <div className="text-xl font-bold leading-none tracking-tight text-primary-800 tabular-nums">
              {listing.price.toFixed(2)}
              <span className="ml-0.5 text-base font-semibold">&euro;</span>
            </div>
            {comparisonInfo?.kind === "previous" && (
              <span className="text-xs leading-none text-gray-400 line-through tabular-nums">
                {comparisonInfo.amount.toFixed(2)}&euro;
              </span>
            )}
            {comparisonInfo?.kind === "shop" && comparisonInfo.savingsPercent != null && (
              <span className="text-[11px] font-bold leading-none text-emerald-700">
                −{comparisonInfo.savingsPercent}% vs. parduotuvėje ({comparisonInfo.amount.toFixed(2)} €)
              </span>
            )}
            {showFresh && (
              <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-600">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {t("fomo.recent_short", "New")}
              </span>
            )}
          </div>
        )}

        <div className="flex items-center gap-1.5">
          <ConditionBadge condition={listing.condition} />
          <span
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-base leading-none"
            title={languageTitle ?? undefined}
          >
            {flag}
          </span>
          <div className="ml-auto">
            {listing.game?.displayRating != null && (
              <RatingBadge
                rating={listing.game.displayRating}
                source={listing.game.ratingSource ?? null}
              />
            )}
          </div>
        </div>

        <div className="flex min-h-[1rem] items-center gap-1.5 text-xs text-gray-500">
          {listing.shippingAvailable ? (
            <>
              <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zm10 0a2 2 0 11-4 0 2 2 0 014 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16V5a1 1 0 00-1-1H4a1 1 0 00-1 1v11h2m7 0h2m4 0h2v-5l-3-4h-5" />
              </svg>
              <span>{t("listings.delivery_label")}</span>
            </>
          ) : listing.location ? (
            <>
              <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="truncate">{listing.location}</span>
            </>
          ) : null}
        </div>

        <div className="mt-auto flex items-center gap-2 pt-1 text-xs text-gray-500">
          <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-800 text-[10px] font-semibold text-white">
            {listing.seller?.displayName?.[0]?.toUpperCase() || "?"}
          </div>
          <span className="truncate">{listing.seller?.displayName}</span>
        </div>
      </div>
    </Link>
  );
}
